<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('potentialCustomers.potentialCustomersLabel') }}</span>
        <h3 class="page-title">{{ $t('potentialCustomers.potentialCustomersList') }}</h3>
      </d-col>

      <d-col col sm="6" class="text-right">
        <d-button
          theme="success"
          class="ml-2 text-nowrap"
          :disabled="downloadInProgress"
          @click.prevent.stop="downloadPotentialCustomers">
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-if="!downloadInProgress">
            <i class="material-icons">cloud_download</i>
            {{ $t('customer.exportCustomers') }}
          </template>
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="potential-customers-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('potentialCustomers.potentialCustomersSearchPlaceholder')"
          v-model="filters.search" />

        <date-range-picker
          :locale-data="{
            'firstDay': 1,
            'format': 'DD-MM-YYYY',
            'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
            'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
          }"
          class="ml-2 w-25"
          style="min-width: 300px;"
          :singleDatePicker="false"
          :timePicker="false"
          :showWeekNumbers="false"
          :showDropdowns="false"
          :autoApply="true"
          v-model="period"
          :ranges="false"
          :linkedCalendars="true">
          <template v-slot:input="picker" style="min-width: 300px;">
            {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
          </template>
        </date-range-picker>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('ui.name') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.emailAddress') }}</th>
            <th scope="col" class="border-0">{{ $t('customer.telephone') }}</th>
            <th scope="col" class="border-0">{{ $t('potentialCustomers.otherData') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.lastContactAt') }}</th>
            <th scope="col" class="border-0">{{ $t('potentialCustomers.activityStartAt') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(customer, index) of customers"
            :key="'customer-' + index">
            <td class="align-middle">
              {{ customer.name }}
            </td>
            <td class="align-middle">
              {{ customer.email }}
            </td>
            <td class="align-middle">
              {{ customer.phone }}
            </td>
            <td class="align-middle">
              <small>
                <template v-if="customer.tax_number">
                  {{ customer.tax_number }}<br>
                </template>

                <template v-if="customer.tax_number">
                  {{ customer.city }}<br>
                </template>

                <template v-if="customer.tax_number">
                  {{ customer.street }} {{ customer.building }}
                  <template v-if="customer.locale"> / {{ customer.locale }}</template><br>
                </template>

                <template v-if="customer.tax_number">
                  {{ customer.postcode }}<br>
                </template>
              </small>
            </td>
            <td class="align-middle">
              {{ customer.last_contact_at }}
            </td>
            <td class="align-middle">
              {{ customer.activity_start_at }}
            </td>

            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/potencjalni-klienci/podglad/' + customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">visibility</i>
              </d-button>
              <d-button
                v-if="userIsAdmin"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removePotentialCustomer(customer.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="customers.length === 0">
            <td
              colspan="7"
              class="text-center">
              {{ $t('potentialCustomers.noPotentialCustomersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'users',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination
  },
  computed: {
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    }
  },
  data() {
    return {
      customers: [],
      downloadInProgress: false,
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: ''
      },
      period: {
        startDate: null,
        endDate: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      if (this.period.startDate && this.period.endDate) {
        where.last_contact_from = [
          this.period.startDate.getFullYear(),
          this.period.startDate.getMonth() + 1,
          this.period.startDate.getDate()
        ].map(n => n < 10 ? '0' + n : n).join('-');
        where.last_contact_to = [
          this.period.endDate.getFullYear(),
          this.period.endDate.getMonth() + 1,
          this.period.endDate.getDate()
        ].map(n => n < 10 ? '0' + n : n).join('-');
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/potential-customers/items?page=' + this.currentPage,
        listField: 'customers',
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: ''
      };
      this.period = {
        startDate: null,
        endDate: null
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['potential-customers-filters-form'], this.debouncedLoadFilteredData);
    },
    removePotentialCustomer (customerID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/potential-customers/delete/',
        id: customerID,
        successTitle: this.$t('potentialCustomers.deleteSuccessTitle'),
        successText: this.$t('potentialCustomers.deleteSuccessText'),
        confirmTitle: this.$t('potentialCustomers.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('potentialCustomers.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    downloadPotentialCustomers () {
      if (this.downloadInProgress) {
        return;
      }

      this.downloadInProgress = true;

      this.$http.post('/potential-customers/export', {}, {
        responseType: 'blob'
      }).then((response) => {
        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', this.$t('potentialCustomers.exportFilename') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(() => {
        this.downloadInProgress = false;
      });
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  }
};
</script>
